import { useEffect, useState, useMemo, useCallback } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { Upload, Sparkles, FileText, Check, X, Edit3, Download, Clock, AlertTriangle, ShieldCheck, MessageSquare } from "lucide-react";
import api from "@/lib/api";
import AppLayout, { Crumbs } from "@/components/AppLayout";
import { StatusBadge } from "@/pages/Dashboard";

const STATUS_FLOW = [
  { id: "new", label: "New" },
  { id: "assigned", label: "Assigned" },
  { id: "document_collection", label: "Documents" },
  { id: "redaction", label: "Redaction" },
  { id: "legal_review", label: "Legal Review" },
  { id: "approved", label: "Approved" },
  { id: "responded", label: "Responded" },
];

export default function CaseDetail() {
  const { caseId } = useParams();
  const [data, setData] = useState(null);
  const [selectedDoc, setSelectedDoc] = useState(null);
  const [selectedRedactionId, setSelectedRedactionId] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [summaryBusy, setSummaryBusy] = useState(false);
  const [note, setNote] = useState("");

  const refresh = useCallback(async () => {
    const { data } = await api.get(`/cases/${caseId}`);
    setData(data);
    if (!selectedDoc && data.documents?.[0]) setSelectedDoc(data.documents[0].document_id);
  }, [caseId, selectedDoc]);

  useEffect(() => { refresh(); }, [caseId]);

  if (!data) return <AppLayout><div className="p-10 text-slate-500">Loading…</div></AppLayout>;
  const c = data.case;
  const currentDoc = data.documents.find((d) => d.document_id === selectedDoc) || data.documents[0];
  const redactionsForDoc = (c.redactions || []).filter((r) => !currentDoc || r.document_id === currentDoc.document_id);
  const stats = {
    total: (c.redactions || []).length,
    pending: (c.redactions || []).filter((r) => r.status === "pending").length,
    approved: (c.redactions || []).filter((r) => r.status === "approved").length,
    rejected: (c.redactions || []).filter((r) => r.status === "rejected").length,
  };

  const daysLeft = Math.ceil((new Date(c.deadline) - new Date()) / 86400000);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/cases/${c.case_id}/documents`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      await refresh();
    } finally { setUploading(false); e.target.value = ""; }
  };

  const handleScan = async () => {
    setScanning(true);
    try {
      await api.post(`/cases/${c.case_id}/scan`);
      await refresh();
    } catch (e) {
      alert(e.response?.data?.detail || "AI scan failed");
    } finally { setScanning(false); }
  };

  const updateRedaction = async (rid, status, modified_text) => {
    await api.patch(`/cases/${c.case_id}/redactions/${rid}`, { status, modified_text });
    await refresh();
  };

  const updateStatus = async (status) => {
    await api.patch(`/cases/${c.case_id}`, { status });
    await refresh();
  };

  const submitNote = async () => {
    if (!note.trim()) return;
    await api.patch(`/cases/${c.case_id}`, { note });
    setNote("");
    await refresh();
  };

  const genSummary = async () => {
    setSummaryBusy(true);
    try {
      await api.post(`/cases/${c.case_id}/summary`);
      await refresh();
    } catch (e) {
      alert(e.response?.data?.detail || "Summary generation failed");
    } finally { setSummaryBusy(false); }
  };

  const downloadAudit = () => {
    const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
    window.open(`${BACKEND_URL}/api/cases/${c.case_id}/audit/export`, "_blank");
  };

  return (
    <AppLayout>
      <div className="p-8 lg:p-10 max-w-[1500px]">
        <Crumbs items={[{ label: "Cases", to: "/cases" }, { label: c.case_number }]} />

        {/* Header */}
        <div className="fa-card p-6 mb-6">
          <div className="flex items-start justify-between gap-6 flex-wrap">
            <div className="min-w-0">
              <div className="flex items-center gap-3 mb-2 flex-wrap">
                <span className="font-mono text-xs text-slate-500" data-testid="case-number">{c.case_number}</span>
                <StatusBadge status={c.status} />
                <span className={`fa-badge fa-badge-${c.priority === "high" ? "danger" : c.priority === "low" ? "neutral" : "info"}`}>{c.priority}</span>
                {c.is_sample && <span className="fa-badge fa-badge-info">Sample case</span>}
              </div>
              <h1 className="font-serif text-3xl tracking-tight font-medium mb-2">{c.subject}</h1>
              <div className="text-sm text-slate-600">Requested by <span className="font-semibold text-slate-900">{c.requester_name}</span></div>
            </div>
            <div className="text-right">
              <div className="flex items-center gap-2 justify-end text-sm">
                <Clock className="w-4 h-4 text-slate-400" />
                <span className={daysLeft < 0 ? "text-red-700 font-semibold" : daysLeft <= 3 ? "text-amber-700 font-semibold" : "text-slate-700"} data-testid="case-deadline">
                  {daysLeft < 0 ? `${Math.abs(daysLeft)} days overdue` : `${daysLeft} days remaining`}
                </span>
              </div>
              <div className="text-xs text-slate-500 mt-1">Due {new Date(c.deadline).toLocaleDateString()}</div>
            </div>
          </div>
          <div className="text-sm text-slate-700 mt-4 pt-4 border-t border-slate-200">{c.description}</div>
        </div>

        {/* Status pipeline */}
        <div className="fa-card p-6 mb-6">
          <div className="text-xs uppercase tracking-widest text-slate-500 mb-4">Status pipeline</div>
          <div className="flex items-center gap-0 flex-wrap">
            {STATUS_FLOW.map((s, i) => {
              const active = c.status === s.id;
              const past = STATUS_FLOW.findIndex((x) => x.id === c.status) > i;
              return (
                <button
                  key={s.id}
                  data-testid={`status-step-${s.id}`}
                  onClick={() => updateStatus(s.id)}
                  className={`flex items-center gap-2 px-3 py-2 text-xs font-medium border ${active ? "bg-[#0B1B3D] text-white border-[#0B1B3D]" : past ? "bg-slate-100 text-slate-700 border-slate-200" : "bg-white text-slate-500 border-slate-200 hover:border-slate-300"}`}
                >
                  {past && <Check className="w-3 h-3" />} {s.label}
                </button>
              );
            })}
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          {/* Left: docs & viewer */}
          <div className="lg:col-span-8 space-y-6">
            <div className="fa-card">
              <div className="flex items-center justify-between p-4 border-b border-slate-200">
                <div className="text-xs uppercase tracking-widest text-slate-500">Documents ({data.documents.length})</div>
                <div className="flex items-center gap-2">
                  <label className="fa-btn-secondary cursor-pointer" data-testid="upload-doc-button">
                    <Upload className="w-4 h-4" /> {uploading ? "Uploading…" : "Upload"}
                    <input type="file" accept=".pdf,.docx,.txt" hidden onChange={handleUpload} />
                  </label>
                  <button
                    data-testid="ai-scan-button"
                    onClick={handleScan}
                    disabled={scanning || data.documents.length === 0}
                    className="fa-btn-primary"
                  >
                    <Sparkles className="w-4 h-4" /> {scanning ? "Analyzing…" : "Run AI Scan"}
                  </button>
                </div>
              </div>
              {data.documents.length === 0 ? (
                <div className="p-12 text-center text-slate-500 text-sm">Upload PDF, DOCX, or TXT documents to begin.</div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-4 min-h-[500px]">
                  <div className="md:col-span-1 border-r border-slate-200 divider-y">
                    {data.documents.map((d) => (
                      <button
                        key={d.document_id}
                        onClick={() => setSelectedDoc(d.document_id)}
                        data-testid={`doc-${d.document_id}`}
                        className={`w-full text-left p-3 text-xs hover:bg-slate-50 ${selectedDoc === d.document_id ? "bg-[#0B1B3D]/5 border-l-2 border-l-[#0B1B3D]" : ""}`}
                      >
                        <div className="flex items-center gap-2 mb-1">
                          <FileText className="w-3.5 h-3.5 text-slate-500" />
                          <span className="font-medium truncate">{d.filename}</span>
                        </div>
                        <div className="text-slate-500">{d.scanned ? "Scanned" : "Not scanned"}</div>
                      </button>
                    ))}
                  </div>
                  <div className="md:col-span-3 p-6 bg-slate-100 overflow-auto" data-testid="redaction-viewer">
                    {currentDoc ? (
                      <RedactionDoc text={currentDoc.text} redactions={redactionsForDoc} selectedId={selectedRedactionId} onSelect={setSelectedRedactionId} />
                    ) : (
                      <div className="text-slate-500 text-sm">No document selected.</div>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Notes */}
            <div className="fa-card p-6">
              <div className="text-xs uppercase tracking-widest text-slate-500 mb-3">Internal notes & communication log</div>
              <div className="space-y-3 mb-4">
                {(c.notes || []).length === 0 ? (
                  <div className="text-sm text-slate-500">No notes yet.</div>
                ) : c.notes.slice().reverse().map((n) => (
                  <div key={n.note_id} className="border-l-2 border-[#C5A059] pl-3 py-1">
                    <div className="text-xs text-slate-500 mb-0.5">{n.author_name} · {new Date(n.created_at).toLocaleString()}</div>
                    <div className="text-sm">{n.text}</div>
                  </div>
                ))}
              </div>
              <div className="flex gap-2">
                <input data-testid="note-input" value={note} onChange={(e) => setNote(e.target.value)} placeholder="Add an internal note…" className="fa-input flex-1" />
                <button data-testid="note-submit" onClick={submitNote} className="fa-btn-secondary"><MessageSquare className="w-4 h-4" /> Add</button>
              </div>
            </div>
          </div>

          {/* Right: redactions panel + actions */}
          <div className="lg:col-span-4 space-y-6">
            <div className="fa-card p-6">
              <div className="text-xs uppercase tracking-widest text-slate-500 mb-4">Redaction summary</div>
              <div className="grid grid-cols-2 gap-3 mb-4">
                <Stat label="Flagged" value={stats.total} />
                <Stat label="Pending" value={stats.pending} tone="warning" />
                <Stat label="Approved" value={stats.approved} tone="success" />
                <Stat label="Rejected" value={stats.rejected} tone="neutral" />
              </div>
            </div>

            <div className="fa-card">
              <div className="p-4 border-b border-slate-200 text-xs uppercase tracking-widest text-slate-500">AI Flags ({redactionsForDoc.length})</div>
              <div className="max-h-[500px] overflow-auto">
                {redactionsForDoc.length === 0 ? (
                  <div className="p-6 text-center text-slate-500 text-sm">No flags yet. Run an AI scan to detect sensitive content.</div>
                ) : redactionsForDoc.map((r) => (
                  <FlagItem
                    key={r.id}
                    r={r}
                    selected={selectedRedactionId === r.id}
                    onClick={() => setSelectedRedactionId(r.id)}
                    onApprove={() => updateRedaction(r.id, "approved")}
                    onReject={() => updateRedaction(r.id, "rejected")}
                    onModify={(text) => updateRedaction(r.id, "modified", text)}
                  />
                ))}
              </div>
            </div>

            <div className="fa-card p-6">
              <div className="text-xs uppercase tracking-widest text-slate-500 mb-3">Finalize</div>
              <div className="space-y-2">
                <button onClick={genSummary} disabled={summaryBusy || stats.approved === 0} className="fa-btn-secondary w-full justify-center" data-testid="gen-summary-button">
                  <Sparkles className="w-4 h-4" /> {summaryBusy ? "Generating…" : "Generate legal summary"}
                </button>
                <button onClick={downloadAudit} className="fa-btn-secondary w-full justify-center" data-testid="export-audit-button">
                  <Download className="w-4 h-4" /> Export audit PDF
                </button>
                {c.status !== "responded" && (
                  <button onClick={() => updateStatus("responded")} className="fa-btn-primary w-full justify-center" data-testid="mark-responded-button">
                    <ShieldCheck className="w-4 h-4" /> Mark as Responded
                  </button>
                )}
              </div>
            </div>

            {c.legal_summary && (
              <div className="fa-card p-6">
                <div className="text-xs uppercase tracking-widest text-slate-500 mb-3">Legal Summary</div>
                <div className="text-sm leading-relaxed whitespace-pre-wrap text-slate-700" data-testid="legal-summary-text">{c.legal_summary}</div>
              </div>
            )}
          </div>
        </div>
      </div>
    </AppLayout>
  );
}

function Stat({ label, value, tone }) {
  const color = tone === "warning" ? "text-amber-700" : tone === "success" ? "text-emerald-700" : tone === "neutral" ? "text-slate-700" : "text-slate-900";
  return (
    <div className="border border-slate-200 p-3">
      <div className="text-xs uppercase tracking-widest text-slate-500">{label}</div>
      <div className={`font-serif text-2xl ${color}`}>{value}</div>
    </div>
  );
}

function FlagItem({ r, selected, onClick, onApprove, onReject, onModify }) {
  const [editing, setEditing] = useState(false);
  const [text, setText] = useState(r.modified_text || "[REDACTED]");
  return (
    <div
      onClick={onClick}
      data-testid={`flag-${r.id}`}
      className={`border-b border-slate-200 p-4 cursor-pointer ${selected ? "bg-slate-50" : "hover:bg-slate-50"}`}
    >
      <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
        <span className="font-mono text-[10px] uppercase tracking-wider px-2 py-0.5 bg-[#0B1B3D] text-white">{r.exemption}</span>
        <span className="text-xs text-slate-500">conf. {(r.confidence || 0).toFixed(2)}</span>
      </div>
      <div className="text-xs font-mono bg-slate-100 p-2 mb-2 border border-slate-200 break-words" data-testid={`flag-${r.id}-text`}>
        &quot;{r.text}&quot;
      </div>
      <div className="text-xs text-slate-600 mb-3 leading-relaxed">{r.rationale}</div>

      {editing ? (
        <div className="space-y-2 mb-2">
          <input value={text} onChange={(e) => setText(e.target.value)} className="fa-input text-xs" />
          <div className="flex gap-2">
            <button onClick={(e) => { e.stopPropagation(); onModify(text); setEditing(false); }} className="fa-btn-primary text-xs px-2 py-1" data-testid={`flag-${r.id}-save`}>Save</button>
            <button onClick={(e) => { e.stopPropagation(); setEditing(false); }} className="fa-btn-secondary text-xs px-2 py-1">Cancel</button>
          </div>
        </div>
      ) : (
        <div className="flex items-center gap-2 flex-wrap">
          <button
            onClick={(e) => { e.stopPropagation(); onApprove(); }}
            disabled={r.status === "approved"}
            className={`fa-btn-secondary text-xs px-2 py-1 ${r.status === "approved" ? "!bg-emerald-50 !border-emerald-200 !text-emerald-700" : ""}`}
            data-testid={`flag-${r.id}-approve`}
          >
            <Check className="w-3 h-3" /> Approve
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); onReject(); }}
            disabled={r.status === "rejected"}
            className={`fa-btn-secondary text-xs px-2 py-1 ${r.status === "rejected" ? "!bg-red-50 !border-red-200 !text-red-700" : ""}`}
            data-testid={`flag-${r.id}-reject`}
          >
            <X className="w-3 h-3" /> Reject
          </button>
          <button onClick={(e) => { e.stopPropagation(); setEditing(true); }} className="fa-btn-secondary text-xs px-2 py-1" data-testid={`flag-${r.id}-modify`}>
            <Edit3 className="w-3 h-3" /> Modify
          </button>
        </div>
      )}
      <div className="mt-2 text-[11px] text-slate-500 capitalize">Status: <span className="font-semibold">{r.status}</span></div>
    </div>
  );
}

function RedactionDoc({ text, redactions, selectedId, onSelect }) {
  // Build segments — apply only redactions with valid coordinates and approved/pending status
  const segments = useMemo(() => {
    const validReds = (redactions || [])
      .filter((r) => r.start >= 0 && r.end > r.start)
      .sort((a, b) => a.start - b.start);
    const segs = [];
    let cursor = 0;
    for (const r of validReds) {
      if (r.start > cursor) segs.push({ type: "text", text: text.slice(cursor, r.start) });
      segs.push({ type: "red", r });
      cursor = Math.max(cursor, r.end);
    }
    if (cursor < text.length) segs.push({ type: "text", text: text.slice(cursor) });
    return segs;
  }, [text, redactions]);

  return (
    <div className="bg-white border border-slate-300 shadow-xl p-10 max-w-3xl mx-auto" style={{ minHeight: "500px" }}>
      <div className="font-serif text-xs uppercase tracking-widest text-slate-400 mb-6">Document preview</div>
      <div className="whitespace-pre-wrap text-sm font-mono leading-relaxed text-slate-900">
        {segments.map((s, i) =>
          s.type === "text" ? (
            <span key={i}>{s.text}</span>
          ) : (
            <span
              key={i}
              onClick={(e) => { e.stopPropagation(); onSelect(s.r.id); }}
              className={`redaction-block ${s.r.status} ${selectedId === s.r.id ? "selected" : ""}`}
              title={`${s.r.exemption} — ${s.r.rationale}`}
            >
              {s.r.status === "approved" ? "█".repeat(Math.max(4, s.r.text.length)) : s.r.status === "modified" ? (s.r.modified_text || "[REDACTED]") : s.r.text}
            </span>
          )
        )}
      </div>
    </div>
  );
}
